import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';

import PageMeta from '../../components/PageMeta';
import CustomPagination from '../../components/common/CustomPagination';
import useDebounce from '../../hooks/useDebounce';
import {
  getProductRequest,
  getProductRequests,
  updateProductRequest,
  updateProductRequestItem,
} from '../../services/productRequest';


const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'awaiting_payment', label: 'Awaiting Payment' },
  { value: 'under_review', label: 'Under Review' },
  { value: 'processing', label: 'Processing' },
  { value: 'added_to_cart', label: 'Added to Cart' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const ITEM_STATUS_OPTIONS = [
  { value: 'submitted', label: 'Submitted' },
  { value: 'under_review', label: 'Under Review' },
  { value: 'processing', label: 'Processing' },
  { value: 'added_to_cart', label: 'Added to Cart' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const MODE_OPTIONS = [
  { value: '', label: 'All Modes' },
  { value: 'link', label: 'Link' },
  { value: 'image', label: 'Image' },
];

const STATUS_LABELS = {
  submitted: 'Submitted',
  awaiting_payment: 'Awaiting Payment',
  under_review: 'Under Review',
  processing: 'Processing',
  added_to_cart: 'Added to Cart',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

const getStatusChipColor = (status) => {
  if (status === 'awaiting_payment') return 'warning';
  if (status === 'completed' || status === 'added_to_cart') return 'success';
  if (status === 'cancelled') return 'error';
  if (status === 'under_review') return 'secondary';
  return 'primary';
};

const formatDateTime = (value) => {
  if (!value) return 'N/A';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'N/A';
  return parsed.toLocaleString();
};

const buildItemDrafts = (productRequest) => (
  (productRequest?.items || []).reduce((accumulator, item) => {
    accumulator[item.id] = {
      status: item.status,
      admin_note: item.admin_note || '',
    };
    return accumulator;
  }, {})
);

const ProductRequestsPage = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [customRowsInput, setCustomRowsInput] = useState('');
  const [customRowsActive, setCustomRowsActive] = useState(false);
  const [filters, setFilters] = useState({ search: '', status: '', request_mode: '' });
  const [detailsModal, setDetailsModal] = useState({ open: false, loading: false, request: null });
  const [adminNote, setAdminNote] = useState('');
  const [itemDrafts, setItemDrafts] = useState({});
  const [savingRequestNote, setSavingRequestNote] = useState(false);
  const [savingItemId, setSavingItemId] = useState(null);
  const debouncedSearch = useDebounce(filters.search, 350);

  const loadRequests = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getProductRequests({
        page: page + 1,
        page_size: rowsPerPage,
        search: debouncedSearch,
        status: filters.status,
        request_mode: filters.request_mode,
      });
      setRows(response.results || []);
      setRowCount(Number(response.count || 0));
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, filters.request_mode, filters.status, page, rowsPerPage]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  const handleFilterChange = (field, value) => {
    setPage(0);
    setFilters((previous) => ({ ...previous, [field]: value }));
  };

  const handleOpenDetails = async (row) => {
    setDetailsModal({ open: true, loading: true, request: null });
    const response = await getProductRequest(row.id);
    setDetailsModal({ open: true, loading: false, request: response });
    setAdminNote(response?.admin_note || '');
    setItemDrafts(buildItemDrafts(response));
  };

  const handleCloseDetails = () => {
    setDetailsModal({ open: false, loading: false, request: null });
    setAdminNote('');
    setItemDrafts({});
    setSavingRequestNote(false);
    setSavingItemId(null);
  };

  const handleRequestNoteSave = async () => {
    if (!detailsModal.request) return;
    setSavingRequestNote(true);
    try {
      const response = await updateProductRequest(detailsModal.request.id, {
        admin_note: adminNote,
      });
      setDetailsModal((previous) => ({ ...previous, request: response.product_request }));
      setItemDrafts(buildItemDrafts(response.product_request));
      loadRequests();
    } finally {
      setSavingRequestNote(false);
    }
  };

  const handleItemDraftChange = (itemId, field, value) => {
    setItemDrafts((previous) => ({
      ...previous,
      [itemId]: {
        ...previous[itemId],
        [field]: value,
      },
    }));
  };

  const handleItemSave = async (itemId) => {
    if (!detailsModal.request || !itemDrafts[itemId]) return;
    setSavingItemId(itemId);
    try {
      const draft = itemDrafts[itemId];
      const response = await updateProductRequestItem(detailsModal.request.id, itemId, {
        status: draft.status,
        admin_note: draft.admin_note,
      });
      setDetailsModal((previous) => ({ ...previous, request: response.product_request }));
      setAdminNote(response.product_request?.admin_note || '');
      setItemDrafts(buildItemDrafts(response.product_request));
      loadRequests();
    } finally {
      setSavingItemId(null);
    }
  };

  const columns = useMemo(() => ([
    {
      field: 'request_number',
      headerName: 'Request',
      minWidth: 180,
      flex: 1,
    },
    {
      field: 'user',
      headerName: 'User',
      minWidth: 220,
      flex: 1.2,
      valueGetter: (_value, row) => row.user?.email || row.user?.username || row.user?.phone || 'N/A',
    },
    {
      field: 'request_mode',
      headerName: 'Mode',
      width: 120,
      valueGetter: (_value, row) => row.request_mode === 'image' ? 'Image' : 'Link',
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 170,
      renderCell: ({ row }) => (
        <Chip
          size="small"
          color={getStatusChipColor(row.status)}
          label={STATUS_LABELS[row.status] || row.status}
        />
      ),
    },
    {
      field: 'total_items',
      headerName: 'Items',
      width: 90,
    },
    {
      field: 'commitment_fee_amount',
      headerName: 'Fee',
      width: 120,
      valueFormatter: (value) => `₦${Number(value || 0).toLocaleString()}`,
    },
    {
      field: 'commitment_fee_paid_at',
      headerName: 'Paid',
      minWidth: 170,
      flex: 1,
      valueFormatter: (value) => formatDateTime(value),
    },
    {
      field: 'created_at',
      headerName: 'Created',
      minWidth: 180,
      flex: 1,
      valueFormatter: (value) => formatDateTime(value),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      minWidth: 130,
      sortable: false,
      renderCell: ({ row }) => (
        <Button variant="outlined" size="small" onClick={() => handleOpenDetails(row)}>
          View
        </Button>
      ),
    },
  ]), []);

  const totalPages = Math.max(1, Math.ceil(rowCount / rowsPerPage));

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: 'background.default',
        px: { xs: 0.75, sm: 1, md: 1.5 },
        py: { xs: 2, sm: 2.5, md: 3 },
        maxWidth: '95vw',
        overflowX: 'hidden',
      }}
    >
      <PageMeta title="Product Request Management" description="Manage app product requests" />

      <Grid container spacing={{ xs: 1.5, md: 2 }}>
        <Grid size={{ xs: 12 }}>
          <Box
            sx={{
              mb: 1,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              backgroundColor: 'primary.main',
              color: 'primary.contrastText',
              borderRadius: 2.5,
              px: { xs: 1.5, sm: 2, md: 3 },
              py: { xs: 2, sm: 2.25 },
            }}
          >
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              Product Request Management
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)' }}>
              Review each requested item independently while the request status updates from the combined outcome.
            </Typography>
          </Box>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Card sx={{ backgroundColor: 'background.paper' }}>
            <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
              <Grid container spacing={{ xs: 1.5, md: 2 }}>
                <Grid size={{ xs: 12, md: 5 }}>
                  <TextField
                    fullWidth
                    label="Search request or user"
                    value={filters.search}
                    onChange={(event) => handleFilterChange('search', event.target.value)}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3.5 }}>
                  <TextField
                    select
                    fullWidth
                    label="Status"
                    value={filters.status}
                    onChange={(event) => handleFilterChange('status', event.target.value)}
                  >
                    {STATUS_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3.5 }}>
                  <TextField
                    select
                    fullWidth
                    label="Mode"
                    value={filters.request_mode}
                    onChange={(event) => handleFilterChange('request_mode', event.target.value)}
                  >
                    {MODE_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Card>
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ minHeight: 650, overflowX: 'auto' }}>
                <DataGrid
                  rows={rows}
                  columns={columns}
                  loading={loading}
                  getRowId={(row) => row.id}
                  disableRowSelectionOnClick
                  hideFooterPagination
                  rowCount={rowCount}
                  autoHeight={false}
                  disableColumnMenu
                  disableColumnFilter
                  disableColumnSelector
                  sx={{
                    border: 0,
                    minWidth: 900,
                    '& .MuiDataGrid-columnHeader': {
                      backgroundColor: 'primary.main',
                    },
                    '& .MuiDataGrid-columnHeaderTitle': {
                      color: 'primary.contrastText',
                      fontWeight: 700,
                    },
                    '& .MuiDataGrid-row:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                />
              </Box>
              <CustomPagination
                totalPages={totalPages}
                page={page}
                onPageChange={(nextPage) => setPage(nextPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(event) => {
                  const value = event.target.value;
                  if (value === 'custom') {
                    setCustomRowsActive(true);
                    setCustomRowsInput(rowsPerPage.toString());
                    return;
                  }
                  setRowsPerPage(Number(value));
                  setPage(0);
                  setCustomRowsActive(false);
                }}
                customRowsActive={customRowsActive}
                customRowsInput={customRowsInput}
                onCustomRowsInputChange={(event) => setCustomRowsInput(event.target.value.replace(/\D/g, ''))}
                onCustomRowsInputBlur={() => {
                  const value = parseInt(customRowsInput, 10);
                  if (value && value > 0) {
                    setRowsPerPage(value);
                    setPage(0);
                  }
                  setCustomRowsActive(false);
                }}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Dialog open={detailsModal.open} onClose={handleCloseDetails} maxWidth="md" fullWidth>
        <DialogTitle>Product Request Details</DialogTitle>
        <DialogContent dividers>
          {detailsModal.loading || !detailsModal.request ? (
            <Typography>Loading...</Typography>
          ) : (
            <Stack spacing={2}>
              <Stack spacing={1}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  {detailsModal.request.request_number}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Typography variant="body2">Summary Status:</Typography>
                  <Chip
                    size="small"
                    color={getStatusChipColor(detailsModal.request.status)}
                    label={STATUS_LABELS[detailsModal.request.status] || detailsModal.request.status}
                  />
                </Stack>
                <Typography variant="body2">
                  User: {detailsModal.request.user?.email || detailsModal.request.user?.username || 'N/A'}
                </Typography>
                <Typography variant="body2">
                  Fee: ₦{Number(detailsModal.request.commitment_fee_amount || 0).toLocaleString()}
                </Typography>
                <Typography variant="body2">
                  Paid At: {formatDateTime(detailsModal.request.commitment_fee_paid_at)}
                </Typography>
                {detailsModal.request.user_note ? (
                  <Typography variant="body2">User Note: {detailsModal.request.user_note}</Typography>
                ) : null}
                <Typography variant="caption" color="text.secondary">
                  The summary status is calculated from the item statuses below.
                </Typography>
              </Stack>

              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Admin Note"
                value={adminNote}
                onChange={(event) => setAdminNote(event.target.value)}
              />

              <Stack direction="row" spacing={1}>
                <Button variant="contained" onClick={handleRequestNoteSave} disabled={savingRequestNote}>
                  {savingRequestNote ? 'Saving...' : 'Save Request Note'}
                </Button>
              </Stack>

              {detailsModal.request.items?.map((item, index) => (
                <Card key={item.id} variant="outlined">
                  <CardContent>
                    <Stack spacing={1.5}>
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={1}
                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                        justifyContent="space-between"
                      >
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                            Item {index + 1}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Item ID: {item.id}
                          </Typography>
                        </Box>
                        <Chip
                          size="small"
                          color={getStatusChipColor(item.status)}
                          label={STATUS_LABELS[item.status] || item.status}
                        />
                      </Stack>

                      <Typography variant="body2">Platform: {item.source_platform}</Typography>
                      {item.source_url ? <Typography variant="body2">Link: {item.source_url}</Typography> : null}
                      <Typography variant="body2">Quantity: {item.quantity}</Typography>
                      {item.color ? <Typography variant="body2">Color: {item.color}</Typography> : null}
                      {item.size ? <Typography variant="body2">Size: {item.size}</Typography> : null}
                      {item.specification ? <Typography variant="body2">Specification: {item.specification}</Typography> : null}
                      {item.note ? <Typography variant="body2">Note: {item.note}</Typography> : null}
                      <Typography variant="caption" color="text.secondary">
                        Use Item ID {item.id} on Manual Item Add when this specific request item is added to cart.
                      </Typography>

                      <Grid container spacing={1.5}>
                        <Grid size={{ xs: 12, md: 4 }}>
                          <TextField
                            select
                            fullWidth
                            label="Item Status"
                            value={itemDrafts[item.id]?.status || item.status}
                            onChange={(event) => handleItemDraftChange(item.id, 'status', event.target.value)}
                          >
                            {ITEM_STATUS_OPTIONS.map((option) => (
                              <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                        <Grid size={{ xs: 12, md: 8 }}>
                          <TextField
                            fullWidth
                            multiline
                            minRows={2}
                            label="Item Admin Note"
                            value={itemDrafts[item.id]?.admin_note || ''}
                            onChange={(event) => handleItemDraftChange(item.id, 'admin_note', event.target.value)}
                          />
                        </Grid>
                      </Grid>

                      <Stack direction="row" spacing={1}>
                        <Button
                          variant="outlined"
                          onClick={() => handleItemSave(item.id)}
                          disabled={savingItemId === item.id}
                        >
                          {savingItemId === item.id ? 'Saving...' : 'Save Item Update'}
                        </Button>
                      </Stack>

                      {!!item.images?.length && (
                        <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
                          {item.images.map((image) => (
                            <Box
                              key={image.id}
                              component="img"
                              src={image.image_url}
                              alt="Product request"
                              sx={{ width: 88, height: 88, borderRadius: 2, objectFit: 'cover' }}
                            />
                          ))}
                        </Stack>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ProductRequestsPage;
